import { ChangeEvent, DropdownItem, ForcedCase, Table, TableRow, Textbox, TextboxProps } from "@mcleod/components";

export interface LocationByTypeProps {
    cityType?: string;
    stateType?: string;
    zoneType?: string;
    locationType?: string;
    marketAreaType?: string;
    countyType?: string;
    useDefault: boolean;
    lookupProps?: {
        all?: Partial<TextboxProps>;
        city?: Partial<TextboxProps>;
        location?: Partial<TextboxProps>;
        state?: Partial<TextboxProps>;
        zone?: Partial<TextboxProps>;
        marketArea?: Partial<TextboxProps>;
        freeform?: Partial<TextboxProps>;
    }
}

const CITY_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "common/city-suggestion",
    lookupModelLayout: "common/CityLookup",
    lookupModelAllowSearchAll: false,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: false,
    lookupModelMaxResults: 10,
    lookupModelResultField: "id",
    lookupModelDisplayField: "full_csz_name",
    lookupModelMinChars: 2,
    forcedCase: ForcedCase.UPPER,
}

const LOCATION_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "lme/dispatch/location-lookup",
    lookupModelLayout: "lme/dispatch/LocationLookup",
    lookupModelResultField: "id",
    lookupModelDisplayField: "name",
    lookupModelMinChars: 3,
    lookupModelMaxResults: 200,
    forcedCase: ForcedCase.NONE,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: true,
    lookupModelAllowSearchAll: true
}

const STATE_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "common/states",
    lookupModelLayout: "common/States",
    lookupModelResultField: "id",
    lookupModelDisplayField: "id",
    lookupModelMinChars: 2,
    forcedCase: ForcedCase.UPPER,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: true,
    lookupModelAllowSearchAll: true,
    quickInfoLayout: "common/StateQuickInfo"
}

const ZONE_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "lme/general/zones",
    lookupModelLayout: "lme/general/Zones",
    lookupModelResultField: "id",
    lookupModelDisplayField: "descr",
    lookupModelMinChars: 1,
    forcedCase: ForcedCase.NONE,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: true,
    lookupModelAllowSearchAll: true
}

const MARKET_AREA_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "lme/general/market-area",
    lookupModelLayout: "lme/general/MarketAreaLookup",
    lookupModelResultField: "id",
    lookupModelDisplayField: "descr",
    lookupModelMinChars: 2,
    forcedCase: ForcedCase.UPPER,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: true,
    lookupModelAllowSearchAll: true
}

const DEFAULT_FREEFORM: Partial<TextboxProps> = {
    lookupModel: null,
    lookupModelLayout: null,
    lookupModelResultField: null,
    lookupModelDisplayField: null,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: false,
    lookupModelAllowSearchAll: true,
    forcedCase: ForcedCase.NONE
}

export class LocationByTypeUtil {
    /**
     * Sets up location type change listener for forms, alerts, and reports.
     * Use setupLocationTypeChangeListenerForTableRow for table rows with existing data.
     */
    public static setupLocationTypeChangeListener(
        typeComponent: Textbox, 
        valueComponent: Textbox, 
        props: LocationByTypeProps
    ): void {
        this.setLookupByType(typeComponent.selectedItem, valueComponent, false, props);
        typeComponent.addChangeListener((event: ChangeEvent) => {
            this.setLookupByType(typeComponent.selectedItem, valueComponent, event.userInitiatedChange, props);
        });
    }

    /**
     * Sets up location type change listener for table rows with existing data.
     * Automatically re-triggers data display to parse multi-select values with the correct delimiter.
     */
    public static setupLocationTypeChangeListenerForTableRow(
        typeComponent: Textbox, 
        valueComponent: Textbox, 
        props: LocationByTypeProps,
        tableRow: TableRow,
        table: Table
    ): void {
        this.setLookupByType(typeComponent.selectedItem, valueComponent, false, props);
        
        // Re-trigger data display to parse multi-select values with new delimiter
        const modelRow = tableRow.data;
        if (modelRow) {
            const allData = table.dataSource?.data || [];
            const rowIndex = allData.indexOf(modelRow);
            valueComponent.displayComponentData(modelRow, allData, rowIndex);
        }
        
        typeComponent.addChangeListener((event: ChangeEvent) => {
            this.setLookupByType(typeComponent.selectedItem, valueComponent, event.userInitiatedChange, props);
        });
    }

    public static setZoneLookup(textbox: Textbox, props: LocationByTypeProps) {
        textbox.setProps(ZONE_LOOKUP);
        textbox.caption = "Zone Lookup";
        if(props?.lookupProps?.zone) textbox.setProps(props.lookupProps.zone);
    }

    public static setStateLookup(textbox: Textbox, props: LocationByTypeProps) {
        textbox.setProps(STATE_LOOKUP);
        textbox.caption = "State Lookup";
        if(props?.lookupProps?.state) textbox.setProps(props.lookupProps.state);
    }

    public static setLocationLookup(textbox: Textbox, props: LocationByTypeProps) {
        textbox.setProps(LOCATION_LOOKUP);
        textbox.caption = "Location Lookup";
        if(props?.lookupProps?.location) textbox.setProps(props.lookupProps.location);
    }

    public static setCityLookup(textbox: Textbox, props: LocationByTypeProps) {
        textbox.setProps(CITY_LOOKUP);
        textbox.caption = "City Lookup";
        if(props?.lookupProps?.city) textbox.setProps(props.lookupProps.city);
    }

    public static setMarketAreaLookup(textbox: Textbox, props: LocationByTypeProps) {
        textbox.setProps(MARKET_AREA_LOOKUP);
        textbox.caption = "Market Area Lookup";
        if(props?.lookupProps?.marketArea) textbox.setProps(props.lookupProps.marketArea);
    }

    public static setDefaultFreeform(textbox: Textbox, lookupType: string, props: LocationByTypeProps) {
        textbox.setProps(DEFAULT_FREEFORM);
        textbox.caption = "C" === lookupType ? "Zip Code" : "";
        if(props?.lookupProps?.freeform) textbox.setProps(props.lookupProps.freeform);
    }

    public static setLookupByType(item: DropdownItem, textbox: Textbox, userInitiatedChange: boolean, props: LocationByTypeProps) {
        const lookupType = typeof item === "string" ? item : item?.value;
        if (userInitiatedChange) {
            textbox.clear();
        }
        if (props.cityType && lookupType === props.cityType) {
            this.setCityLookup(textbox, props);
        } else if (props.stateType && lookupType === props.stateType) {
            this.setStateLookup(textbox, props);
        } else if (props.zoneType && lookupType === props.zoneType) {
            this.setZoneLookup(textbox, props);
        } else if (props.locationType && lookupType === props.locationType) {
            this.setLocationLookup(textbox, props);
        } else if (props.marketAreaType && lookupType === props.marketAreaType) {
            this.setMarketAreaLookup(textbox, props);
        } else if ((props.countyType && lookupType === props.countyType) || props.useDefault) {
            this.setDefaultFreeform(textbox, lookupType, props);
        }

        if(props?.lookupProps?.all) textbox.setProps(props.lookupProps.all);
    }
}
